import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  Box,
  Container,
  Heading,
  Text,
  Button,
  Divider,
  Input,
  SimpleGrid,
  Icon,
  Grid,
  VStack,
  HStack,
  Flex,
  InputGroup,
  InputRightElement,
  IconButton,
  Collapse,
} from '@chakra-ui/react';
import {
  FaUsers,
  FaRedo,
  FaGift,
  FaFilter,
  FaMapMarkerAlt,
  FaCheck,
} from 'react-icons/fa';

import { CloseIcon } from '@chakra-ui/icons';
import Helmet from 'react-helmet';
import EventCard from './EventCard';
import EventCardSkeleton from './EventCardSkeleton';
import { getNextOccurrence } from '../utils';

/** Normalizza una data ISO a YYYY-MM-DD per evitare warning di deprecazione (formato richiesto in future versioni). */
function toDateOnly(isoOrDateStr) {
  if (isoOrDateStr == null || typeof isoOrDateStr !== 'string')
    return isoOrDateStr;
  return isoOrDateStr.slice(0, 10);
}

// --- Custom debounce hook ---
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedValue(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debouncedValue;
}

export default function Attivita({ eventsData }) {
  // --- Filtering states for upcoming events ---
  // 1. Leggi i parametri:
  const [searchParams, setSearchParams] = useSearchParams();
  const pfQuery = searchParams.get('partecipantiFilter') || '';
  const cfQuery = searchParams.get('categoryFilter') || '';

  const [searchQuery, setSearchQuery] = useState('');
  const [showFreeEvents, setShowFreeEvents] = useState(false);
  const [showRecurring, setShowRecurring] = useState(false);
  const [partecipantiFilter, setPartecipantiFilter] = useState(pfQuery);
  const [locationFilter, setLocationFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState(cfQuery);
  // Stato per aprire/chiudere il pannello filtri
  const [isFiltersOpen, setIsFiltersOpen] = useState(pfQuery);

  // Use debounce so that search fires only once every 400ms
  const debouncedSearchQuery = useDebounce(searchQuery, 400);

  // --- Upcoming events state with infinite loading ---
  const [upcomingEvents, setUpcomingEvents] = useState(eventsData);
  const [locationItems, setLocationItems] = useState([]);
  const [partecipantiItems, setPartecipantiItems] = useState([]);
  const [categoryItems, setCategoryItems] = useState([]);
  const [loadingUpcoming, setLoadingUpcoming] = useState(!eventsData.length);
  const [currentPageUpcoming, setCurrentPageUpcoming] = useState(1);
  const upcomingPageSize = window.innerWidth < 788 ? 6 : 9;
  const [totalPagesUpcoming, setTotalPagesUpcoming] = useState(1);

  // Ref for intersection observer trigger for upcoming events
  const loaderRefUpcoming = useRef(null);

  // When upcoming filters change, reset the list and page number
  useEffect(() => {
    setUpcomingEvents([]);
    setCurrentPageUpcoming(1);
  }, [
    debouncedSearchQuery,
    showFreeEvents,
    showRecurring,
    locationFilter,
    partecipantiFilter,
    categoryFilter,
  ]);

  // --- Fetch upcoming events when filters or page change ---
  useEffect(() => {
    const fetchUpcoming = async () => {
      setLoadingUpcoming(true);
      try {
        const params = new URLSearchParams();
        params.append('past', 'false');
        params.append('page', currentPageUpcoming);
        params.append('pageSize', upcomingPageSize);
        if (debouncedSearchQuery)
          params.append('searchQuery', debouncedSearchQuery);
        if (locationFilter) {
          params.append('locationFilter', locationFilter);
        }
        if (showFreeEvents) params.append('showFreeEvents', 'true');
        if (showRecurring) params.append('showRecurring', 'true');
        if (partecipantiFilter)
          params.append('partecipantiFilter', partecipantiFilter);
        if (categoryFilter) params.append('categoryFilter', categoryFilter);

        const response = await fetch(`/api/eventi?${params.toString()}`);

        if (!response.ok) {
          throw new Error(
            `HTTP ${response.status}: ${response.statusText || 'Server error'}`
          );
        }

        const contentType = response.headers.get('content-type');
        if (!contentType || !contentType.includes('application/json')) {
          const text = await response.text();
          throw new Error(
            `Expected JSON but got ${contentType || 'unknown content type'}. Response: ${text.substring(0, 100)}`
          );
        }

        const result = await response.json();
        if (currentPageUpcoming === 1) {
          setUpcomingEvents(result.events);
          setLocationItems(result.locations || []);
          setPartecipantiItems(result.participants || []);
          setCategoryItems(result.categories || []);
        } else {
          setUpcomingEvents((prev) => [...prev, ...result.events]);
        }
        setTotalPagesUpcoming(result.meta.pagination.pageCount);
      } catch (error) {
        console.error('Error fetching upcoming events', error);
      } finally {
        setLoadingUpcoming(false);
      }
    };

    fetchUpcoming();
  }, [
    currentPageUpcoming,
    debouncedSearchQuery,
    showFreeEvents,
    showRecurring,
    partecipantiFilter,
    locationFilter,
    categoryFilter,
  ]);

  // Sincronizza lo stato in uscita con la URL
  const updatePartecipantiFilter = (newVal) => {
    setPartecipantiFilter(newVal);
    if (newVal) {
      searchParams.set('partecipantiFilter', newVal);
    } else {
      searchParams.delete('partecipantiFilter');
    }
    setSearchParams(searchParams, { replace: true });
  };

  const updateCategoryFilter = (newVal) => {
    setCategoryFilter(newVal);
    if (newVal) {
      searchParams.set('categoryFilter', newVal);
    } else {
      searchParams.delete('categoryFilter');
    }
    setSearchParams(searchParams, { replace: true });
  };

  // Assicurati di inizializzare lo stato se la URL cambia esternamente
  useEffect(() => {
    if (pfQuery !== partecipantiFilter) {
      setPartecipantiFilter(pfQuery);
      setIsFiltersOpen(true);
    }
  }, [pfQuery]);

  useEffect(() => {
    if (cfQuery !== categoryFilter) {
      setCategoryFilter(cfQuery);
      setIsFiltersOpen(true);
    }
  }, [cfQuery]);

  // --- Infinite loading for upcoming events using IntersectionObserver ---
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          currentPageUpcoming < totalPagesUpcoming
        ) {
          setCurrentPageUpcoming((prev) => prev + 1);
        }
      },
      { threshold: 1 }
    );
    if (loaderRefUpcoming.current) observer.observe(loaderRefUpcoming.current);
    // eslint-disable-next-line consistent-return
    return () => {
      if (loaderRefUpcoming.current)
        observer.unobserve(loaderRefUpcoming.current);
    };
  }, [loadingUpcoming, currentPageUpcoming, totalPagesUpcoming]);

  // --- Color mode and layout values ---
  // Removed unused variables: inputBg, cardBg, cardShadow

  // Category color mapping (same as EventiV2.js)
  const categoryStyleMap = {
    GIOCO: { bg: '#1a8cff', color: 'white' },
    MAGIC: { bg: '#ff8c1a', color: 'white' },
    VARIANTE: { bg: '#b266ff', color: 'white' },
    EVENTO: { bg: '#4a5568', color: 'white' },
    YOUNG: { bg: '#87CEEB', color: 'white' },
    ARTE: { bg: '#e95995', color: 'white' },
    NATURA: { bg: '#3eb297', color: 'white' },
    GENERAL: { bg: '#ffcc00', color: 'black' },
  };

  const getCategoryStyles = (category) => {
    if (!category) return { bg: '#4a5568', color: 'white' };
    const macroKey = category.toUpperCase();
    return categoryStyleMap[macroKey] || { bg: '#4a5568', color: 'white' };
  };

  const sortedUpcomingEvents = upcomingEvents
    .map((event) => {
      const dataOnly = toDateOnly(event.Data);
      const dateEndOnly = toDateOnly(event.Date_end);
      const normalizedEvent = {
        ...event,
        Data: dataOnly ?? event.Data,
        Date_end: dateEndOnly ?? event.Date_end,
      };
      if (event.Recurring) {
        return {
          ...normalizedEvent,
          sortDate: getNextOccurrence(event.Recurring),
        };
      }
      return {
        ...normalizedEvent,
        sortDate: new Date(dataOnly || event.Data),
      };
    })
    .sort((a, b) => a.sortDate - b.sortDate);

  return (
    <>
      <Helmet>
        <title>Attività - CIL - Creatività in Libertà</title>
        <meta property="og:title" content="Attività" />
        <meta
          property="og:description"
          content="Scopri tutte le attività e gli eventi di CIL – Creatività in Libertà. Giochi, laboratori, eventi e molto altro per promuovere creatività, gioco, partecipazione e cura della comunità."
        />
        <meta
          property="og:image"
          content={`${window.location.origin}/logo.png`}
        />
        <meta
          property="og:url"
          content={`${window.location.origin}/attivita`}
        />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Attività" />
        <meta
          name="twitter:description"
          content="Scopri tutte le attività e gli eventi di CIL – Creatività in Libertà. Giochi, laboratori, eventi e molto altro per promuovere creatività, gioco, partecipazione e cura della comunità."
        />
        <meta
          name="twitter:image"
          content={`${window.location.origin}/logo.png`}
        />
      </Helmet>
      <Box bg="#36393f" minH="100vh" overflowX="hidden" p={4} color="white">
        <Box
          bg="#2f3136"
          boxShadow="xl"
          borderRadius="20px"
          px={6}
          py={4}
          mt={6}
          border="1px solid"
          borderColor="gray.600"
        >
          <Flex direction="row" align="center" justify="space-between">
            {/* Sezione Ricerca */}
            <HStack spacing={2}>
              <InputGroup maxWidth={{ base: '350px', md: '600px' }}>
                <Input
                  placeholder="Cerca eventi..."
                  bg="#36393f"
                  color="white"
                  borderRadius="12px"
                  boxShadow="sm"
                  border="1px solid"
                  borderColor="gray.600"
                  _placeholder={{ color: 'gray.400' }}
                  _focus={{
                    borderColor: '#ffcc00',
                    boxShadow: '0 0 0 1px #ffcc00',
                  }}
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  minWidth={{ base: '0', md: '400px' }}
                />
                {searchQuery && (
                  <InputRightElement>
                    <IconButton
                      size="sm"
                      variant="ghost"
                      aria-label="Exit search"
                      icon={<CloseIcon />}
                      onClick={() => setSearchQuery('')}
                    />
                  </InputRightElement>
                )}
              </InputGroup>
            </HStack>

            {/* Pulsante Filtri */}
            <Button
              display={{ base: 'none', sm: 'flex' }}
              leftIcon={<FaFilter />}
              bg="#ffcc00"
              color="black"
              variant="solid"
              size="md"
              borderRadius="12px"
              fontWeight="600"
              _hover={{
                bg: '#e6b800',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(255, 204, 0, 0.3)',
              }}
              transition="all 0.3s ease"
              onClick={() => setIsFiltersOpen((prev) => !prev)}
            >
              Filtra Eventi
            </Button>
            <IconButton
              display={{ base: 'flex', sm: 'none' }}
              size="md"
              variant="solid"
              bg="#ffcc00"
              color="black"
              borderRadius="12px"
              _hover={{
                bg: '#e6b800',
                transform: 'translateY(-2px)',
                boxShadow: '0 8px 25px rgba(255, 204, 0, 0.3)',
              }}
              transition="all 0.3s ease"
              onClick={() => setIsFiltersOpen((prev) => !prev)}
              icon={<FaFilter />}
            />
          </Flex>

          {/* Pannello a Card */}
          <Collapse in={isFiltersOpen} animateOpacity>
            <Box
              mt={6}
              p={{ base: 6, md: 8 }}
              bg="#36393f"
              borderRadius="20px"
              boxShadow="xl"
              border="1px solid"
              borderColor="gray.600"
            >
              <Grid templateColumns="1fr" gap={{ base: 4, md: 4 }}>
                {/* Sezione Partecipanti */}
                <Flex flexDirection="row">
                  <Flex alignItems="center" mb={0} mr={{ base: 2, md: 0 }}>
                    <Icon as={FaUsers} mr={2} boxSize={5} color="#ffcc00" />
                    <Text
                      fontSize="lg"
                      fontWeight="semibold"
                      display={{ base: 'none', md: 'block' }}
                      mr={3}
                      color="white"
                    >
                      Partecipanti:
                    </Text>
                  </Flex>
                  <Flex wrap="wrap" gap={2}>
                    {partecipantiItems.map((opt) => (
                      <Button
                        key={opt}
                        variant="outline"
                        size="sm"
                        borderRadius="12px"
                        borderColor="gray.600"
                        bg={
                          partecipantiFilter === opt ? '#ffcc00' : 'transparent'
                        }
                        color={partecipantiFilter === opt ? 'black' : 'white'}
                        _hover={{
                          bg:
                            partecipantiFilter === opt ? '#e6b800' : 'gray.600',
                          borderColor: '#ffcc00',
                          transform: 'translateY(-2px)',
                        }}
                        _visited={{ bg: 'transparent' }}
                        transition="all 0.3s ease"
                        onClick={() =>
                          updatePartecipantiFilter(
                            partecipantiFilter === opt ? '' : opt
                          )
                        }
                      >
                        {opt}
                      </Button>
                    ))}
                  </Flex>
                </Flex>

                <Divider borderColor="gray.600" />
                {/* Sezione Località */}
                <Flex flexDirection="row">
                  <Flex alignItems="center" mb={0} mr={{ base: 2, md: 0 }}>
                    <Icon
                      as={FaMapMarkerAlt}
                      mr={2}
                      boxSize={5}
                      color="#ffcc00"
                    />
                    <Text
                      fontSize="lg"
                      mr={3}
                      fontWeight="semibold"
                      display={{ base: 'none', md: 'block' }}
                      color="white"
                    >
                      Località:
                    </Text>
                  </Flex>
                  <Flex wrap="wrap" gap={2}>
                    {locationItems.map((loc) => (
                      <Button
                        key={loc}
                        variant="outline"
                        size="sm"
                        borderRadius="12px"
                        borderColor="gray.600"
                        bg={locationFilter === loc ? '#ffcc00' : 'transparent'}
                        color={locationFilter === loc ? 'black' : 'white'}
                        _hover={{
                          bg: locationFilter === loc ? '#e6b800' : 'gray.600',
                          borderColor: '#ffcc00',
                          transform: 'translateY(-2px)',
                        }}
                        transition="all 0.3s ease"
                        onClick={() =>
                          setLocationFilter(locationFilter === loc ? '' : loc)
                        }
                      >
                        {loc}
                      </Button>
                    ))}
                  </Flex>
                </Flex>
              </Grid>
              <Divider my={6} borderColor="gray.600" />
              {/* Toggles per Gratuiti e Ricorrenti */}
              <Flex mt={6} gap={4} justifyContent="flex-start">
                <Button
                  variant="solid"
                  size="md"
                  leftIcon={<FaGift />}
                  onClick={() => setShowFreeEvents((prev) => !prev)}
                  rightIcon={showFreeEvents ? <FaCheck /> : null}
                  bg={showFreeEvents ? '#ffcc00' : '#2f3136'}
                  color={showFreeEvents ? 'black' : 'white'}
                  border="1px solid"
                  borderColor={showFreeEvents ? '#ffcc00' : 'gray.600'}
                  borderRadius="12px"
                  fontWeight="600"
                  _hover={{
                    bg: showFreeEvents ? '#e6b800' : 'gray.600',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(255, 204, 0, 0.3)',
                  }}
                  transition="all 0.3s ease"
                >
                  Gratuiti
                </Button>
                <Button
                  variant="solid"
                  size="md"
                  leftIcon={<FaRedo />}
                  onClick={() => setShowRecurring((prev) => !prev)}
                  rightIcon={showRecurring ? <FaCheck /> : null}
                  bg={showRecurring ? '#ffcc00' : '#2f3136'}
                  color={showRecurring ? 'black' : 'white'}
                  border="1px solid"
                  borderColor={showRecurring ? '#ffcc00' : 'gray.600'}
                  borderRadius="12px"
                  fontWeight="600"
                  _hover={{
                    bg: showRecurring ? '#e6b800' : 'gray.600',
                    transform: 'translateY(-2px)',
                    boxShadow: '0 8px 25px rgba(255, 204, 0, 0.3)',
                  }}
                  transition="all 0.3s ease"
                >
                  Ricorrenti
                </Button>
              </Flex>
            </Box>
          </Collapse>
        </Box>

        {/* Filtro categorie sopra la lista eventi (nessun titolo / nessun contenitore a blocco) */}
        {categoryItems.length > 0 && (
          <Flex
            as="span"
            display="flex"
            flexWrap="wrap"
            gap={2}
            mt={6}
            width="100%"
            justifyContent="center"
            alignItems="center"
          >
            {categoryItems.map((cat) => {
              const categoryStyles = getCategoryStyles(cat);
              const isSelected = categoryFilter === cat;
              return (
                <Button
                  key={cat}
                  variant="outline"
                  size="sm"
                  borderRadius="12px"
                  borderColor={categoryStyles.bg}
                  bg={isSelected ? categoryStyles.bg : categoryStyles.bg}
                  color={categoryStyles.color}
                  opacity={isSelected ? 1 : 0.7}
                  _hover={{
                    bg: categoryStyles.bg,
                    borderColor: categoryStyles.bg,
                    color: categoryStyles.color,
                    opacity: 1,
                    transform: 'translateY(-2px)',
                  }}
                  transition="all 0.3s ease"
                  onClick={() =>
                    updateCategoryFilter(categoryFilter === cat ? '' : cat)
                  }
                >
                  {cat}
                </Button>
              );
            })}
          </Flex>
        )}

        {/* Upcoming Events */}
        <Container py={6} width="100%" maxWidth="unset" px="0">
          <SimpleGrid
            columns={{ sm: 1, md: 2, lg: 3 }}
            spacing={{ base: 12, md: 12 }}
          >
            {sortedUpcomingEvents.map((event, index) => (
              <EventCard
                key={event.documentId}
                event={event}
                index={index}
                showDescription
              />
            ))}
            {loadingUpcoming && currentPageUpcoming === 1 && (
              <>
                <EventCardSkeleton index={1} />
                <EventCardSkeleton index={2} />
                <EventCardSkeleton index={3} />
              </>
            )}
          </SimpleGrid>
          {/* Loader trigger for upcoming events */}
          <Box ref={loaderRefUpcoming} textAlign="center" py={4}>
            {loadingUpcoming && <EventCardSkeleton index={1} />}
          </Box>
          {upcomingEvents.length === 0 && !loadingUpcoming && (
            <Box textAlign="center" mt={4}>
              <Text color="gray.600">Nessun evento trovato.</Text>
            </Box>
          )}
        </Container>

        {/* CTA per proporre progetti */}
        <Box
          textAlign="center"
          bg="#2f3136"
          borderRadius="20px"
          p={8}
          boxShadow="xl"
          border="1px solid"
          borderColor="gray.600"
          mb={12}
        >
          <VStack spacing={6}>
            <Box
              w="20"
              h="20"
              bg="#ffcc00"
              borderRadius="full"
              display="flex"
              alignItems="center"
              justifyContent="center"
            >
              <Icon as={FaUsers} w={10} h={10} color="black" />
            </Box>

            <VStack spacing={3}>
              <Heading
                as="h3"
                fontSize={{ base: 'xl', md: '2xl' }}
                color="white"
                fontWeight="bold"
                fontFamily="Poppins, sans-serif"
              >
                Hai un&apos;idea per un progetto?
              </Heading>

              <Text
                fontSize={{ base: 'md', md: 'lg' }}
                color="gray.300"
                lineHeight="1.6"
                fontFamily="Montserrat, sans-serif"
                maxW="2xl"
              >
                CIL è sempre alla ricerca di nuove idee e progetti creativi! Se
                hai un&apos;idea per un&apos;attività, un laboratorio o un
                evento, contattaci e raccontaci la tua proposta.
              </Text>
            </VStack>

            <Button
              as="a"
              href="/contattaci"
              bg="#ffcc00"
              color="black"
              size="lg"
              leftIcon={<FaUsers />}
              _hover={{
                bg: '#ffcc00dd',
                transform: 'scale(1.05)',
              }}
              _active={{
                bg: '#ffcc00dd',
                transform: 'scale(0.98)',
              }}
              fontFamily="Montserrat, sans-serif"
              fontWeight="bold"
              px={8}
              py={4}
              fontSize="lg"
            >
              Proponi il tuo progetto
            </Button>
          </VStack>
        </Box>
      </Box>
    </>
  );
}
